/**
 * Compare a branch with main, then merge or discard (RFC 0313 3k, 3l, canvas 7).
 *
 * Comparing writes nothing to main. An overlap, a paragraph or field changed on
 * both sides, shows both versions and waits for a pick; Merge stays disabled
 * until every one has one. Merge saves a version of main first. Discard throws
 * the branch away, and main never read it, so there is nothing to undo.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  compareBranch,
  type DocumentComparison,
  discardBranch,
  type FieldChange,
  findBranch,
  type Hunk,
  mergeBranch,
  pickOverlap,
  projectDocuments,
  updateFromMain,
} from '@mod/core/model'
import { useEffect, useState } from 'react'
import { setProjectBranch } from '../../services/branches/branchSelection'
import { addedMark, removedMark } from '../../services/history/historyRows'
import { readLocalPerson } from '../../services/identity/localPerson'
import { useDocument } from '../../services/world/repo/useDocument'

/**
 * A changed paragraph against its old self, word by word: the words that
 * stayed, then what went and what came. Pure, so the rule is testable.
 */
export function wordDiff(
  before: string,
  after: string,
): { head: string; removed: string; added: string; tail: string } {
  const a = before.split(/(\s+)/)
  const b = after.split(/(\s+)/)
  let h = 0
  while (h < a.length && h < b.length && a[h] === b[h]) h++
  let t = 0
  while (
    t < a.length - h &&
    t < b.length - h &&
    a[a.length - 1 - t] === b[b.length - 1 - t]
  )
    t++
  return {
    head: a.slice(0, h).join(''),
    removed: a.slice(h, a.length - t).join(''),
    added: b.slice(h, b.length - t).join(''),
    tail: a.slice(a.length - t).join(''),
  }
}

/** A hunk is named by what it changed; two identical hunks are one edit. */
const hunkKey = (h: Hunk) => `${h.removed.join('|')}>${h.added.join('|')}`

// The same marks a document's History uses, so a diff reads the same here.
const del = removedMark
const ins = addedMark

/** One hunk: a rewritten paragraph inline, or whole paragraphs that came or went. */
function HunkView({ hunk }: { hunk: Hunk }) {
  if (hunk.removed.length === 1 && hunk.added.length === 1) {
    const d = wordDiff(hunk.removed[0] as string, hunk.added[0] as string)
    return (
      <p className="whitespace-pre-wrap text-foreground/80">
        {d.head}
        {d.removed ? <span className={del}>{d.removed}</span> : null}
        {d.added ? <span className={ins}>{d.added}</span> : null}
        {d.tail}
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-1">
      {hunk.removed.map((p) => (
        <p key={`r${p}`} className="whitespace-pre-wrap">
          <span className={del}>{p}</span>
        </p>
      ))}
      {hunk.added.map((p) => (
        <p key={`a${p}`} className="whitespace-pre-wrap">
          <span className={ins}>{p}</span>
        </p>
      ))}
    </div>
  )
}

function FieldLine({ f }: { f: FieldChange }) {
  if (f.kind === 'list') {
    return (
      <p className="text-foreground/80">
        {f.path}:{' '}
        {f.added.map((a) => (
          <span key={`a${a}`} className={`${ins} mr-1`}>
            {a}
          </span>
        ))}
        {f.removed.map((r) => (
          <span key={`r${r}`} className={`${del} mr-1`}>
            {r}
          </span>
        ))}
      </p>
    )
  }
  const show = (v: unknown) =>
    v === undefined ? '' : typeof v === 'string' ? v : JSON.stringify(v)
  return (
    <p className="text-foreground/80">
      {f.path}: <span className={del}>{show(f.before)}</span>{' '}
      <span className={ins}>{show(f.after)}</span>
    </p>
  )
}

export function BranchView({
  projectId,
  name,
  onOpenDocument,
}: {
  projectId: string
  name: string
  onOpenDocument?: (id: string) => void
}) {
  const repo = useRepo() as unknown as Repo
  const [projectDoc] = useDocument(projectId as AnyDocumentId)
  const branch = findBranch(projectDoc, name)
  const [rows, setRows] = useState<DocumentComparison[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const titles = new Map(
    projectDocuments(projectDoc).map((d) => [d.id, d.title]),
  )

  // Recompared whenever the project moves: a pick, a copy, a merge step.
  useEffect(() => {
    let cancelled = false
    if (!projectDoc) return
    void compareBranch(repo, projectId, name).then((r) => {
      if (!cancelled) setRows(r)
    })
    return () => {
      cancelled = true
    }
  }, [repo, projectId, name, projectDoc])

  if (!branch) {
    return (
      <p className="p-4 text-muted-foreground text-xs">No branch {name}.</p>
    )
  }
  const closed = branch.status !== 'open'
  const blocked = (rows ?? []).some((r) => !r.resolved)
  const act = (f: () => Promise<string>) => {
    setBusy(true)
    void f()
      .then(setNote)
      .catch((e) => setNote(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false))
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto">
      <div className="flex items-center gap-2 border-border border-b px-4 py-2 text-xs">
        <span className="font-medium text-foreground">{name}</span>
        <span className="text-muted-foreground">
          {closed ? branch.status : 'into main'}
        </span>
      </div>
      <div className="px-4 pt-3 pb-1 text-[11px] text-muted-foreground">
        Documents
      </div>
      {rows === null ? (
        <p className="px-4 text-muted-foreground text-xs">Comparing…</p>
      ) : !rows.some(
          (r) =>
            r.branchHunks.length || r.fieldChanges.length || r.overlaps.length,
        ) ? (
        <p className="px-4 text-muted-foreground text-xs">
          Nothing written on this branch yet.
        </p>
      ) : null}
      {/* Only what the branch changed: a document opened on the branch makes
          a copy, but an unchanged copy is nothing to compare. */}
      {(rows ?? [])
        .filter(
          (r) =>
            r.branchHunks.length || r.fieldChanges.length || r.overlaps.length,
        )
        .map((r) => (
          <div
            key={r.documentId}
            className="mx-3 mb-2 rounded-lg border border-border/70 bg-card px-3 py-2"
          >
            <div className="flex items-center gap-2 text-[13px]">
              <button
                type="button"
                onClick={() => onOpenDocument?.(r.documentId)}
                className="min-w-0 flex-1 truncate text-left text-foreground hover:underline"
              >
                {titles.get(r.documentId) ?? r.documentId}
              </button>
              <span className="text-muted-foreground text-xs">
                {r.text && (r.text.added || r.text.removed) ? (
                  <>
                    <span className="text-green-400">+{r.text.added}</span>{' '}
                    <span className="text-red-400">−{r.text.removed}</span>
                  </>
                ) : null}
                {r.fields?.length ? ` ${r.fields.length} fields` : ''}
              </span>
            </div>
            {/* What the branch changed, as the edits themselves. */}
            {r.branchHunks.length || r.fieldChanges.length ? (
              <div className="mt-2 flex flex-col gap-2 text-xs">
                {r.branchHunks.map((h) => (
                  <HunkView key={`b${hunkKey(h)}`} hunk={h} />
                ))}
                {r.fieldChanges.map((f) => (
                  <FieldLine key={f.path} f={f} />
                ))}
              </div>
            ) : null}
            {/* What main did meanwhile: the merge keeps it alongside. */}
            {r.mainHunks.length ? (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  Main changed {r.mainHunks.length} place
                  {r.mainHunks.length === 1 ? '' : 's'} since this branch opened
                </summary>
                <div className="mt-1 flex flex-col gap-2">
                  {r.mainHunks.map((h) => (
                    <HunkView key={`m${hunkKey(h)}`} hunk={h} />
                  ))}
                </div>
              </details>
            ) : null}
            {r.overlaps.map((o) => {
              const picked =
                branch.picks?.[r.documentId]?.[String(o.index)] ?? null
              return (
                <div
                  key={o.index}
                  className="mt-2 rounded-lg border border-border bg-card p-2 text-xs"
                >
                  <div className="mb-1 text-muted-foreground">
                    Changed on both sides
                  </div>
                  {(['branch', 'main'] as const).map((side) => (
                    <button
                      key={side}
                      type="button"
                      disabled={closed}
                      onClick={() =>
                        void pickOverlap(
                          repo,
                          projectId,
                          name,
                          r.documentId,
                          o.index,
                          side,
                        )
                      }
                      className={`mb-1 block w-full rounded-md border px-2 py-1 text-left ${
                        picked === side
                          ? 'border-foreground'
                          : 'border-border hover:bg-muted'
                      }`}
                    >
                      <span className="mr-2 text-muted-foreground">
                        Keep {side === 'branch' ? name : 'main'}
                      </span>
                      <span className="whitespace-pre-wrap text-foreground/90">
                        {side === 'branch' ? o.branch : o.main}
                      </span>
                    </button>
                  ))}
                </div>
              )
            })}
          </div>
        ))}
      {!closed ? (
        <div className="flex items-center gap-2 px-4 py-3">
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              act(async () => {
                await discardBranch(repo, projectId, name)
                setProjectBranch(projectId, '')
                return 'Discarded. Main is unchanged.'
              })
            }
            className="rounded-full px-3 py-1 text-muted-foreground text-xs hover:text-foreground"
          >
            Discard
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() =>
              act(async () => {
                const out = await updateFromMain(repo, projectId, name)
                if (out.ok) return 'Brought main’s newer work into the branch.'
                return out.reason === 'overlaps'
                  ? 'Pick a side for every overlap first, then update.'
                  : 'This branch is closed.'
              })
            }
            className="rounded-full border border-border px-3 py-1 text-xs hover:bg-muted"
          >
            Update from main
          </button>
          <span className="flex-1" />
          <button
            type="button"
            disabled={busy || blocked || rows === null}
            title={blocked ? 'Pick a side for every overlap first' : undefined}
            onClick={() =>
              act(async () => {
                const out = await mergeBranch(repo, projectId, name, {
                  by: readLocalPerson()?.id ?? 'user',
                })
                if (!out.ok) {
                  return out.reason === 'overlaps'
                    ? 'Pick a side for every overlap first.'
                    : 'This branch is not open.'
                }
                setProjectBranch(projectId, '')
                return `Merged ${out.merged} document${out.merged === 1 ? '' : 's'}. A version of main was saved first.`
              })
            }
            className="rounded-full bg-foreground px-3 py-1 font-medium text-background text-xs disabled:opacity-40"
          >
            Merge
          </button>
        </div>
      ) : null}
      {note ? (
        <p className="px-4 pb-3 text-muted-foreground text-xs">{note}</p>
      ) : null}
    </div>
  )
}
